import { useState } from "react";
import "@/App.css";
import axios from "axios";
import { toast } from "sonner";
import { MagnifyingGlass, Wrench } from "@phosphor-icons/react";
import { NavTabs } from "@/components/NavTabs";
import { SapConnectionStatus } from "@/components/SapConnectionStatus";
import { ErpConnectionStatus } from "@/components/ErpConnectionStatus";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Oct 2026, user's explicit ask ("separate setup/admin menu where we
// search sto number and investigate") after exhausting every remotely
// diagnosable theory (batch locks, quantity/fragmentation, area config,
// human-click race, self-race) for a recurring "missing Source
// Logistics Area" STO failure with no SAP audit trail to fall back on.
// Shows the full combine-attempt call-by-call history plus the
// auto-captured Source-Area forensic snapshots (see
// stock_transfer_service.py's _capture_source_area_diagnostics) - our
// own write attempts + SAP's data state at the moment it got stuck.
// Cannot see human SAP UI clicks or SAP's own internal jobs - same
// blind spot as today, called out plainly in the UI below.
function formatTime(iso) {
  if (!iso) return "-";
  return new Date(iso).toLocaleString();
}

export default function StoDiagnosticsPage() {
  const [stoId, setStoId] = useState("");
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const investigate = async () => {
    if (!stoId.trim()) return;
    setLoading(true);
    setNotFound(false);
    setOrder(null);
    try {
      const { data } = await axios.get(`${API}/admin/stock-transfer/investigate/${stoId.trim()}`);
      setOrder(data);
    } catch (err) {
      if (err?.response?.status === 404) {
        setNotFound(true);
      } else {
        toast.error("Could not load this order", { description: err?.response?.data?.detail || err.message });
      }
    } finally {
      setLoading(false);
    }
  };

  const history = order?.combine_attempt_history || [];
  const diagnostics = order?.source_area_diagnostics || [];

  return (
    <div className="min-h-screen bg-[#F7F8FA]">
      <div className="bg-[#0E7C86] px-4 sm:px-6 py-3 flex items-center gap-3">
        <Wrench size={22} weight="bold" className="text-white shrink-0" />
        <span className="text-white font-heading font-bold text-base shrink-0">Materials Hub</span>
        <NavTabs />
        <SapConnectionStatus />
        <ErpConnectionStatus />
      </div>

      <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-6">
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-[#1D2939] font-heading" data-testid="sto-diagnostics-title">
            STO Diagnostics
          </h1>
          <p className="text-sm text-[#667085] font-sans mt-0.5">
            Search a Stock Transfer Order to see its full combine-attempt history and any auto-captured
            Source-Area forensic snapshots. Scope note: this only shows OUR OWN write attempts and SAP's data
            state at that moment - it cannot see human SAP UI clicks or SAP's own internal background jobs.
          </p>
        </div>

        <div className="flex gap-2 mb-6">
          <Input
            value={stoId}
            onChange={(e) => setStoId(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && investigate()}
            placeholder="e.g. STO-000695"
            className="max-w-[280px]"
            data-testid="sto-diagnostics-search-input"
          />
          <Button onClick={investigate} disabled={loading || !stoId.trim()} data-testid="sto-diagnostics-search-button" className="gap-1.5">
            <MagnifyingGlass size={14} weight="bold" />
            {loading ? "Searching..." : "Investigate"}
          </Button>
        </div>

        {notFound && (
          <div className="bg-white border border-[#D0D5DD] rounded-md px-4 py-6 text-center text-sm text-[#667085]" data-testid="sto-diagnostics-not-found">
            No Stock Transfer Order found with ID "{stoId.trim()}".
          </div>
        )}

        {order && (
          <div className="space-y-6" data-testid="sto-diagnostics-result">
            <div className="bg-white border border-[#D0D5DD] rounded-md p-4">
              <h2 className="text-sm font-semibold text-[#1D2939] mb-2">Order Summary</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <div><div className="text-xs text-[#98A2B3]">Status</div><div className="font-medium">{order.status}</div></div>
                <div><div className="text-xs text-[#98A2B3]">GI Status</div><div className="font-medium">{order.gi_status || "-"}</div></div>
                <div><div className="text-xs text-[#98A2B3]">Route</div><div className="font-medium">{order.ship_from_site_id} &rarr; {order.ship_to_site_id}</div></div>
                <div><div className="text-xs text-[#98A2B3]">SAP Order ID</div><div className="font-medium">{order.sap_order_id || "-"}</div></div>
              </div>
              {order.gi_error && (
                <div className="mt-3 text-sm text-[#B42318] bg-[#FEF3F2] rounded px-3 py-2" data-testid="sto-diagnostics-gi-error">{order.gi_error}</div>
              )}
            </div>

            <div className="bg-white border border-[#D0D5DD] rounded-md p-4">
              <h2 className="text-sm font-semibold text-[#1D2939] mb-3">Combine Attempt History ({history.length})</h2>
              {history.length === 0 ? (
                <div className="text-sm text-[#98A2B3]" data-testid="sto-diagnostics-history-empty">No combine attempts recorded yet for this order.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-[#EAECF0] text-left text-xs text-[#667085]">
                        <th className="px-2 py-1.5 font-medium">Time</th>
                        <th className="px-2 py-1.5 font-medium">Step</th>
                        <th className="px-2 py-1.5 font-medium">Detail</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.slice().reverse().map((h, i) => (
                        <tr key={i} className="border-b border-[#F2F4F7]" data-testid={`sto-diagnostics-history-row-${i}`}>
                          <td className="px-2 py-1.5 text-[#475467] whitespace-nowrap">{formatTime(h.updated_at)}</td>
                          <td className="px-2 py-1.5 font-medium">{h.step || "-"}</td>
                          <td className="px-2 py-1.5 text-[#475467]">{h.detail || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="bg-white border border-[#D0D5DD] rounded-md p-4">
              <h2 className="text-sm font-semibold text-[#1D2939] mb-3">Source-Area Forensic Snapshots ({diagnostics.length})</h2>
              {diagnostics.length === 0 ? (
                <div className="text-sm text-[#98A2B3]" data-testid="sto-diagnostics-snapshots-empty">
                  No missing-Source-Area event has been captured for this order.
                </div>
              ) : (
                <div className="space-y-4">
                  {diagnostics.slice().reverse().map((d, i) => (
                    <div key={i} className="border border-[#EAECF0] rounded p-3" data-testid={`sto-diagnostics-snapshot-${i}`}>
                      <div className="text-xs text-[#98A2B3] mb-1">{formatTime(d.captured_at)} - Task {d.task_id || "-"}</div>
                      <div className="text-sm font-medium mb-2">Missing products: {(d.missing_products || []).join(", ")}</div>
                      <pre className="text-xs bg-[#F7F8FA] rounded p-2 overflow-x-auto whitespace-pre-wrap">{JSON.stringify(d, null, 2)}</pre>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
